import { formatHumanName, type MedplumClient } from '@medplum/core';
import type {
  CareTeam,
  EpisodeOfCare,
  Group,
  Patient,
  Practitioner,
  Reference,
  ServiceRequest,
} from '@medplum/fhirtypes';

import {
  EOC_GROUP_EXT,
  EOC_GROUP_REFERENCE_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  addParticipantIfMissing,
  getCareTeamPatientRef,
  getPractitionerParticipantRefs,
} from '@unify/fhir-model/resources/care-team';
import { getGroupTemplateRef, groupRef, practitionerRef } from '@unify/fhir-model/resources/group';
import { getEpisodeOfCareRef } from '@unify/fhir-model/resources/service-request';
import { toFhirDateOnlyLocal } from '@/lib/dateHelpers';
import { stopTreatmentPlansForEpisode } from '@/lib/chart/stopTreatmentPlansForEpisode';
import { isHumanParticipantDisplay } from '@/pages/appointments/utils/fhirHelpers';
import {
  readTrackerStage,
  upsertTrackerStageTag,
  type TrackerStageCode,
} from '@/lib/serviceRequestFhirConstants';
import { applyStageToStatus } from '@/lib/serviceRequestFhirMapping';
import type { ServiceRequestRequesterReference } from '@/lib/serviceRequestFhirMapping';
import { buildServiceRequestResource } from '@/lib/serviceRequestService';
import {
  requestedTemplateMatchesGroupTemplate,
  setRequestedGroupTemplateRef,
} from './requestedGroupTemplate';

function todayIsoDate(): string {
  return toFhirDateOnlyLocal(new Date());
}

function patientDisplayName(patient: Patient): string | undefined {
  const label = formatHumanName(patient.name?.[0] ?? {}).trim();
  return label || undefined;
}

export function getActiveGroupMemberPatientIds(group: Group): string[] {
  return (group.member ?? [])
    .filter((m) => !m.inactive && m.entity?.reference?.startsWith('Patient/'))
    .map((m) => m.entity!.reference!.replace('Patient/', ''));
}

export type GroupParticipationEndMode =
  /** PRD §10 suspension — EoC onhold, CareTeam stays active. */
  | 'suspend'
  /** Pre-start withdrawal — EoC cancelled. */
  | 'pre-start-withdraw'
  /** Formal discharge — EoC finished, CareTeam inactive. */
  | 'formal-discharge';

/**
 * Ends active Group membership. Default is suspension (onhold), not formal discharge.
 */
export async function unenrollPatientFromGroup(params: {
  medplum: MedplumClient;
  group: Group;
  patientId: string;
  periodEnd?: string;
  mode?: GroupParticipationEndMode;
}): Promise<Group> {
  const {
    medplum,
    group,
    patientId,
    periodEnd = todayIsoDate(),
    mode = 'suspend',
  } = params;
  if (!group.id) throw new Error('Group must have an id.');

  const updatedGroup = removeOrInactivatePatientGroupMembership(group, patientId, periodEnd);
  const savedGroup = await medplum.updateResource({
    ...updatedGroup,
    quantity: getActiveGroupMemberPatientIds(updatedGroup).length,
  });

  const eoc = await findExistingGroupEnrollmentEpisodeOfCare(medplum, patientId, group.id, {
    includeWaitlist: false,
    includeFinished: false,
    includeCancelled: false,
  });
  if (eoc) {
    const eocStatus =
      mode === 'formal-discharge'
        ? 'finished'
        : mode === 'pre-start-withdraw'
          ? 'cancelled'
          : 'onhold';
    const nextEoc = await medplum.updateResource({
      ...eoc,
      status: eocStatus,
      period: {
        ...eoc.period,
        ...(mode === 'formal-discharge' || mode === 'pre-start-withdraw'
          ? { end: periodEnd }
          : {}),
      },
    });

    if (mode === 'formal-discharge' && nextEoc.id) {
      try {
        await stopTreatmentPlansForEpisode(medplum, nextEoc.id, patientId);
      } catch (err) {
        console.warn('[groupEnrollment] failed to stop treatment plans after discharge', err);
      }
    }

    if (mode === 'formal-discharge' && nextEoc.team?.[0]?.reference?.startsWith('CareTeam/')) {
      const careTeamId = nextEoc.team[0].reference.replace('CareTeam/', '');
      const careTeam = await medplum.readResource('CareTeam', careTeamId);
      await medplum.updateResource({ ...careTeam, status: 'inactive' });
    }
  }

  return savedGroup;
}

/** Reactivate an on-hold Group Therapy EoC and restore Group membership (PRD §10). */
export async function returnPatientToGroup(params: {
  medplum: MedplumClient;
  group: Group;
  patientId: string;
  facilitatorIds: string[];
  facilitatorDisplays?: string[];
  organizationId?: string;
  periodStart?: string;
}): Promise<{ group: Group; episodeOfCare: EpisodeOfCare; careTeam: CareTeam }> {
  const {
    medplum,
    group,
    patientId,
    facilitatorIds,
    facilitatorDisplays,
    organizationId,
    periodStart = todayIsoDate(),
  } = params;
  if (!group.id) throw new Error('Group must have an id.');

  const onHold = await findExistingGroupEnrollmentEpisodeOfCare(medplum, patientId, group.id, {
    includeWaitlist: false,
    includeFinished: false,
    includeCancelled: false,
    statuses: ['onhold'],
  });
  if (!onHold) {
    throw new Error('No on-hold Group Therapy episode found to reactivate.');
  }

  const patient = await medplum.readResource('Patient', patientId);
  const facilitatorRefs = facilitatorRefsFromIds(facilitatorIds, facilitatorDisplays);
  const updatedGroup = addPatientToGroupMemberList(
    group,
    patientId,
    periodStart,
    patientDisplayName(patient),
  );
  const savedGroup = await medplum.updateResource({
    ...updatedGroup,
    quantity: getActiveGroupMemberPatientIds(updatedGroup).length,
  });

  let careTeam: CareTeam;
  if (onHold.team?.[0]?.reference?.startsWith('CareTeam/')) {
    const careTeamId = onHold.team[0].reference.replace('CareTeam/', '');
    const existingCt = await medplum.readResource('CareTeam', careTeamId);
    careTeam = await medplum.updateResource(
      createOrUpdateGroupMemberCareTeam({
        existing: { ...existingCt, status: 'active' },
        patient,
        organizationId,
        facilitatorRefs,
        primaryFacilitatorRef: facilitatorRefs[0],
      }),
    );
  } else {
    careTeam = await medplum.createResource(
      createOrUpdateGroupMemberCareTeam({
        patient,
        organizationId,
        facilitatorRefs,
        primaryFacilitatorRef: facilitatorRefs[0],
      }),
    );
  }

  const episodeOfCare = await medplum.updateResource({
    ...onHold,
    status: 'active',
    careManager: facilitatorRefs[0] ?? onHold.careManager,
    team: [{ reference: `CareTeam/${careTeam.id}` }],
    period: { ...onHold.period, start: onHold.period?.start ?? periodStart, end: undefined },
  });

  return { group: savedGroup, episodeOfCare, careTeam };
}

export async function syncGroupFacilitatorsToEnrolledMembers(params: {
  medplum: MedplumClient;
  group: Group;
  facilitatorIds: string[];
  facilitatorDisplays?: string[];
  organizationId?: string;
}): Promise<void> {
  const { medplum, group, facilitatorIds, facilitatorDisplays, organizationId } = params;
  if (!group.id) return;

  const facilitatorRefs = facilitatorRefsFromIds(facilitatorIds, facilitatorDisplays);
  const patientIds = getActiveGroupMemberPatientIds(group);

  await Promise.all(
    patientIds.map(async (patientId) => {
      const eoc = await findExistingGroupEnrollmentEpisodeOfCare(medplum, patientId, group.id!);
      if (!eoc?.team?.[0]?.reference?.startsWith('CareTeam/')) return;
      const careTeamId = eoc.team[0].reference.replace('CareTeam/', '');
      const careTeam = await medplum.readResource('CareTeam', careTeamId);
      const patient = await medplum.readResource('Patient', patientId);
      await medplum.updateResource(
        createOrUpdateGroupMemberCareTeam({
          existing: careTeam,
          patient,
          organizationId,
          facilitatorRefs,
          primaryFacilitatorRef: facilitatorRefs[0],
        }),
      );
      if (facilitatorRefs[0]) {
        await medplum.updateResource({ ...eoc, careManager: facilitatorRefs[0] });
      }
    }),
  );
}

export function addPatientToGroupMemberList(
  group: Group,
  patientId: string,
  periodStart = todayIsoDate(),
  display?: string,
): Group {
  const ref = `Patient/${patientId}`;
  const trimmedDisplay = display?.trim();
  const members = [...(group.member ?? [])];
  const existing = members.find((m) => m.entity?.reference === ref);
  if (existing) {
    return {
      ...group,
      member: members.map((m) =>
        m.entity?.reference === ref
          ? {
              ...m,
              inactive: false,
              period: { ...m.period, start: periodStart },
              entity: {
                ...m.entity,
                reference: ref,
                ...(trimmedDisplay ? { display: trimmedDisplay } : {}),
              },
            }
          : m,
      ),
    };
  }
  return {
    ...group,
    member: [
      ...members,
      {
        entity: {
          reference: ref,
          ...(trimmedDisplay ? { display: trimmedDisplay } : {}),
        },
        period: { start: periodStart },
        inactive: false,
      },
    ],
  };
}

export function removeOrInactivatePatientGroupMembership(
  group: Group,
  patientId: string,
  periodEnd = todayIsoDate(),
): Group {
  const ref = `Patient/${patientId}`;
  return {
    ...group,
    member: (group.member ?? []).map((m) =>
      m.entity?.reference === ref
        ? { ...m, inactive: true, period: { ...m.period, end: periodEnd } }
        : m,
    ),
  };
}

export function setEpisodeOfCareGroupRef(
  resource: EpisodeOfCare,
  groupReference: Reference | undefined,
): EpisodeOfCare {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== EOC_GROUP_EXT && e.url !== EOC_GROUP_REFERENCE_EXT,
  );
  const extension = groupReference
    ? [...others, { url: EOC_GROUP_EXT, valueReference: groupReference }]
    : others;
  return { ...resource, extension: extension.length ? extension : undefined };
}

export function getEpisodeOfCareGroupRef(resource: EpisodeOfCare): Reference | undefined {
  return (
    resource.extension?.find((e) => e.url === EOC_GROUP_EXT)?.valueReference ??
    resource.extension?.find((e) => e.url === EOC_GROUP_REFERENCE_EXT)?.valueReference
  );
}

/**
 * True when `EpisodeOfCare.type[]` carries a Group Therapy service type. Requests are written with
 * `group` and enrollment with `group-therapy`, so both codes count.
 */
function hasGroupTherapyServiceType(eoc: EpisodeOfCare): boolean {
  return !!eoc.type?.some((t) =>
    t.coding?.some(
      (c) =>
        c.system === THERAPY_SERVICE_TYPE_SYSTEM &&
        (c.code === 'group' || c.code === 'group-therapy'),
    ),
  );
}

/** True when EoC is Group Therapy by type code and/or cohort group-reference. */
export function isGroupTherapyEpisodeOfCare(eoc: EpisodeOfCare): boolean {
  return hasGroupTherapyServiceType(eoc) || !!getEpisodeOfCareGroupRef(eoc)?.reference;
}

export async function findExistingGroupEnrollmentEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string,
  groupId: string,
  opts?: {
    includeWaitlist?: boolean;
    includeFinished?: boolean;
    includeCancelled?: boolean;
    statuses?: string[];
  },
): Promise<EpisodeOfCare | undefined> {
  const statusList =
    opts?.statuses ??
    [
      'active',
      'planned',
      'onhold',
      ...(opts?.includeWaitlist ? ['waitlist'] : []),
      ...(opts?.includeFinished ? ['finished'] : []),
      ...(opts?.includeCancelled ? ['cancelled'] : []),
    ];
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: statusList.join(','),
    _count: '50',
  });
  const groupRefStr = `Group/${groupId}`;
  return episodes.find(
    (eoc) =>
      getEpisodeOfCareGroupRef(eoc)?.reference === groupRefStr && hasGroupTherapyServiceType(eoc),
  );
}

/**
 * Enrollment-time episode lookup. Group Therapy uses one EoC per Patient and concrete Group
 * (PRD_GROUP_THERAPY §9), and enrollment "creates **or activates**" it.
 *
 * The request pipeline creates that EoC up front and deliberately leaves the cohort reference off
 * until a cohort is allocated, so matching on the cohort reference alone finds nothing and enrolling
 * would open a second episode next to the one the client's ServiceRequest already points at. An
 * open Group Therapy episode with no cohort yet is therefore adopted rather than duplicated —
 * the same rule the closed-cohort waitlist fill applies server-side.
 */
export async function findGroupEnrollmentEpisodeOfCareToAdopt(
  medplum: MedplumClient,
  patientId: string,
  group: Group,
): Promise<EpisodeOfCare | undefined> {
  const episodes = await medplum.searchResources('EpisodeOfCare', {
    patient: `Patient/${patientId}`,
    status: 'active,planned,onhold,waitlist',
    _count: '50',
  });
  const candidates = episodes.filter(hasGroupTherapyServiceType);

  const linked = candidates.find(
    (eoc) => getEpisodeOfCareGroupRef(eoc)?.reference === `Group/${group.id}`,
  );
  if (linked) return linked;

  const unallocated = candidates.filter((eoc) => !getEpisodeOfCareGroupRef(eoc)?.reference);
  if (unallocated.length <= 1) return unallocated[0];

  // The client has open requests for more than one Group offering, so the cohort reference cannot
  // disambiguate them. Fall back to the waitlist key on each episode's ServiceRequest and adopt the
  // episode that actually asked for this cohort's Group Template.
  const templateReference = getGroupTemplateRef(group)?.reference;
  if (!templateReference) return undefined;
  const requests = await medplum.searchResources('ServiceRequest', {
    subject: `Patient/${patientId}`,
    _count: '50',
  });
  return unallocated.find((eoc) =>
    requests.some(
      (sr) =>
        getEpisodeOfCareRef(sr)?.reference === `EpisodeOfCare/${eoc.id}` &&
        requestedTemplateMatchesGroupTemplate(sr, templateReference),
    ),
  );
}

/** The Group Therapy ServiceRequest whose `episode-of-care` extension points at this episode. */
export async function findGroupTherapyRequestForEpisode(
  medplum: MedplumClient,
  patientId: string,
  episodeOfCareId: string,
): Promise<ServiceRequest | undefined> {
  const requests = await medplum.searchResources('ServiceRequest', {
    subject: `Patient/${patientId}`,
    _count: '50',
  });
  return requests.find(
    (sr) => getEpisodeOfCareRef(sr)?.reference === `EpisodeOfCare/${episodeOfCareId}`,
  );
}

/**
 * Enrollment fulfils the request only once screening has cleared it (PRD §9). `matching` is the
 * stage `saveGroupScreeningQuestionnaireResponse` moves a cleared screening to, so anything earlier
 * keeps its stage and holds the episode short of `active`.
 */
function stageAfterEnrollment(current: TrackerStageCode | undefined): TrackerStageCode {
  if (!current) return 'screening';
  return current === 'matching' || current === 'fulfilled' ? 'fulfilled' : current;
}

export function buildGroupEnrollmentEpisodeOfCare(params: {
  patient: Patient;
  group: Group;
  careTeamRef: Reference<CareTeam>;
  periodStart?: string;
  periodEnd?: string;
  careManagerRef?: Reference<Practitioner>;
  organizationId?: string;
  serviceRequestId?: string;
  status?: EpisodeOfCare['status'];
}): EpisodeOfCare {
  const {
    patient,
    group,
    careTeamRef,
    periodStart,
    periodEnd,
    careManagerRef,
    organizationId,
    serviceRequestId,
    status = 'active',
  } = params;
  const managingOrgId =
    organizationId ??
    (group.managingEntity?.reference?.startsWith('Organization/')
      ? group.managingEntity.reference.replace('Organization/', '')
      : undefined);
  const eoc: EpisodeOfCare = {
    resourceType: 'EpisodeOfCare',
    status,
    patient: { reference: `Patient/${patient.id}` },
    type: [
      {
        coding: [
          {
            system: THERAPY_SERVICE_TYPE_SYSTEM,
            code: 'group-therapy',
            display: 'Group Therapy',
          },
        ],
      },
    ],
    team: [careTeamRef],
    period: {
      start: periodStart ?? todayIsoDate(),
      ...(periodEnd ? { end: periodEnd } : {}),
    },
    ...(careManagerRef ? { careManager: careManagerRef } : {}),
    ...(managingOrgId
      ? { managingOrganization: { reference: `Organization/${managingOrgId}` } }
      : {}),
    ...(serviceRequestId
      ? { referralRequest: [{ reference: `ServiceRequest/${serviceRequestId}` }] }
      : {}),
  };
  return setEpisodeOfCareGroupRef(eoc, groupRef(group.id!, group.name));
}

function careTeamReference(careTeam: CareTeam): Reference<CareTeam> {
  return { reference: `CareTeam/${careTeam.id}` };
}

export function createOrUpdateGroupMemberCareTeam(params: {
  existing?: CareTeam;
  patient: Patient;
  organizationId?: string;
  facilitatorRefs: Reference<Practitioner>[];
  primaryFacilitatorRef?: Reference<Practitioner>;
}): CareTeam {
  const { existing, patient, organizationId, facilitatorRefs, primaryFacilitatorRef } = params;
  let careTeam: CareTeam = existing
    ? { ...existing }
    : {
        resourceType: 'CareTeam',
        status: 'active',
        name: `${patient.name?.[0]?.text ?? 'Client'} — Group care team`,
        subject: { reference: `Patient/${patient.id}` },
        participant: [],
      };

  careTeam.subject = { reference: `Patient/${patient.id}` };
  careTeam.category = [
    {
      coding: [
        {
          system: THERAPY_SERVICE_TYPE_SYSTEM,
          code: 'group-therapy',
          display: 'Group Therapy',
        },
      ],
    },
  ];
  if (organizationId) {
    careTeam.managingOrganization = [{ reference: `Organization/${organizationId}` }];
    careTeam.meta = {
      ...careTeam.meta,
      accounts: [{ reference: `Organization/${organizationId}` }],
    } as CareTeam['meta'];
  }

  const primary = primaryFacilitatorRef ?? facilitatorRefs[0];
  if (primary?.reference) {
    careTeam = addParticipantIfMissing(careTeam, primary, 'PPRF');
  }
  for (const ref of facilitatorRefs) {
    if (ref.reference && ref.reference !== primary?.reference) {
      careTeam = addParticipantIfMissing(careTeam, ref, 'SPRF');
    }
  }

  // Safety: never include other patients
  const subjectRef = getCareTeamPatientRef(careTeam)?.reference;
  careTeam.participant = (careTeam.participant ?? []).filter((p) => {
    const memberRef = p.member?.reference ?? '';
    return !memberRef.startsWith('Patient/') || memberRef === subjectRef;
  });

  return careTeam;
}

export function syncGroupProvidersToMemberCareTeam(
  careTeam: CareTeam,
  facilitatorRefs: Reference<Practitioner>[],
  primaryFacilitatorRef?: Reference<Practitioner>,
): CareTeam {
  const patientRef = getCareTeamPatientRef(careTeam);
  if (!patientRef?.reference?.startsWith('Patient/')) {
    throw new Error('CareTeam subject must be a single Patient.');
  }
  const patientId = patientRef.reference.replace('Patient/', '');
  return createOrUpdateGroupMemberCareTeam({
    existing: careTeam,
    patient: { resourceType: 'Patient', id: patientId },
    facilitatorRefs,
    primaryFacilitatorRef,
  });
}

/** Returns true when careTeam includes any Patient other than the subject. */
export function careTeamIncludesOtherPatients(careTeam: CareTeam): boolean {
  const subjectRef = getCareTeamPatientRef(careTeam)?.reference;
  return (careTeam.participant ?? []).some((p) => {
    const ref = p.member?.reference;
    return ref?.startsWith('Patient/') && ref !== subjectRef;
  });
}

export function facilitatorRefsFromIds(ids: string[], displays?: string[]): Reference<Practitioner>[] {
  // Callers fall back to the raw reference when a facilitator has no display; a
  // `Practitioner/<id>` string must never be persisted as a human-readable name.
  return ids.map((id, idx) => {
    const display = displays?.[idx]?.trim();
    return practitionerRef(
      id,
      isHumanParticipantDisplay(display, id) ? display : undefined,
    ) as Reference<Practitioner>;
  });
}

export function getCareTeamParticipantPatientIds(careTeam: CareTeam): string[] {
  return (careTeam.participant ?? [])
    .map((p) => p.member?.reference)
    .filter((r): r is string => !!r?.startsWith('Patient/'))
    .map((r) => r.replace('Patient/', ''));
}

export function getCareTeamPractitionerIds(careTeam: CareTeam): string[] {
  return getPractitionerParticipantRefs(careTeam)
    .map((r) => r.reference?.replace(/^Practitioner\//, ''))
    .filter((id): id is string => !!id);
}

export async function enrollPatientInGroup(params: {
  medplum: MedplumClient;
  group: Group;
  patient: Patient;
  facilitatorIds: string[];
  facilitatorDisplays?: string[];
  periodStart?: string;
  organizationId?: string;
  requester?: ServiceRequestRequesterReference;
}): Promise<{
  group: Group;
  episodeOfCare: EpisodeOfCare;
  careTeam: CareTeam;
  /** Absent only when reactivating a suspended member, which reuses the existing request. */
  serviceRequest?: ServiceRequest;
}> {
  const {
    medplum,
    group,
    patient,
    facilitatorIds,
    facilitatorDisplays,
    periodStart,
    organizationId,
    requester,
  } = params;
  if (!group.id || !patient.id) {
    throw new Error('Group and Patient must have ids before enrollment.');
  }

  const facilitatorRefs = facilitatorRefsFromIds(facilitatorIds, facilitatorDisplays);

  // Prefer reactivating an on-hold EoC over creating a duplicate.
  const onHold = await findExistingGroupEnrollmentEpisodeOfCare(medplum, patient.id, group.id, {
    statuses: ['onhold'],
  });
  if (onHold) {
    return returnPatientToGroup({
      medplum,
      group,
      patientId: patient.id,
      facilitatorIds,
      facilitatorDisplays,
      organizationId,
      periodStart,
    });
  }

  const updatedGroup = addPatientToGroupMemberList(
    group,
    patient.id,
    periodStart,
    patientDisplayName(patient),
  );
  const savedGroup = await medplum.updateResource({
    ...updatedGroup,
    quantity: getActiveGroupMemberPatientIds(updatedGroup).length,
  });

  const existingEoc = await findGroupEnrollmentEpisodeOfCareToAdopt(medplum, patient.id, group);
  const existingCareTeamRef = existingEoc?.team?.find((t) =>
    t.reference?.startsWith('CareTeam/'),
  )?.reference;
  const existingCareTeam = existingCareTeamRef
    ? await medplum.readResource('CareTeam', existingCareTeamRef.replace('CareTeam/', ''))
    : undefined;

  const careTeamDraft = createOrUpdateGroupMemberCareTeam({
    ...(existingCareTeam ? { existing: existingCareTeam } : {}),
    patient,
    organizationId,
    facilitatorRefs,
    primaryFacilitatorRef: facilitatorRefs[0],
  });
  const careTeam = existingCareTeam
    ? await medplum.updateResource(careTeamDraft)
    : await medplum.createResource(careTeamDraft);

  if (existingEoc) {
    const existingRequest = await findGroupTherapyRequestForEpisode(
      medplum,
      patient.id,
      existingEoc.id!,
    );
    const stage = stageAfterEnrollment(readTrackerStage(existingRequest?.meta));
    const serviceRequest = existingRequest
      ? await advanceGroupTherapyRequest(medplum, existingRequest, stage)
      : await createGroupTherapyEnrollmentRequest(medplum, {
          patient,
          group,
          episodeOfCareId: existingEoc.id!,
          organizationId,
          requester,
          stage,
        });

    // An already-active member must never be walked back to `planned` because their request lags.
    const stagedStatus = applyStageToStatus(stage).episodeOfCareStatus;
    const episodeOfCare = await medplum.updateResource({
      ...setEpisodeOfCareGroupRef(existingEoc, groupRef(group.id, group.name)),
      status: existingEoc.status === 'active' ? 'active' : stagedStatus,
      careManager: facilitatorRefs[0] ?? existingEoc.careManager,
      team: [careTeamReference(careTeam)],
      referralRequest: [{ reference: `ServiceRequest/${serviceRequest.id}` }],
      period: {
        ...existingEoc.period,
        start: existingEoc.period?.start ?? periodStart ?? todayIsoDate(),
      },
    });
    return { group: savedGroup, episodeOfCare, careTeam, serviceRequest };
  }

  // No episode to adopt means the client never came through the request pipeline, so the request
  // that tracks their enrollment has to be opened here — at `screening`, since nothing has screened
  // them yet (PRD §7). `referralRequest` needs the request id, so the episode is written twice.
  const stage = stageAfterEnrollment(undefined);
  const created = await medplum.createResource(
    buildGroupEnrollmentEpisodeOfCare({
      patient,
      group: savedGroup,
      careTeamRef: careTeamReference(careTeam),
      periodStart,
      careManagerRef: facilitatorRefs[0],
      organizationId,
      status: applyStageToStatus(stage).episodeOfCareStatus,
    }),
  );
  const serviceRequest = await createGroupTherapyEnrollmentRequest(medplum, {
    patient,
    group,
    episodeOfCareId: created.id!,
    organizationId,
    requester,
    stage,
  });
  const episodeOfCare = await medplum.updateResource({
    ...created,
    referralRequest: [{ reference: `ServiceRequest/${serviceRequest.id}` }],
  });

  return { group: savedGroup, episodeOfCare, careTeam, serviceRequest };
}

/** Open the Group Therapy request that tracks a roster-added client's enrollment. */
async function createGroupTherapyEnrollmentRequest(
  medplum: MedplumClient,
  params: {
    patient: Patient;
    group: Group;
    episodeOfCareId: string;
    organizationId?: string;
    requester?: ServiceRequestRequesterReference;
    stage: TrackerStageCode;
  },
): Promise<ServiceRequest> {
  const { patient, group, episodeOfCareId, organizationId, requester, stage } = params;
  const draft = buildServiceRequestResource({
    patient,
    therapyTypeCode: 'group-therapy',
    stage,
    requester:
      requester ?? {
        reference: `Patient/${patient.id}`,
        display: patientDisplayName(patient),
      },
    episodeOfCareId,
    ...(organizationId
      ? { performerOrganization: { reference: `Organization/${organizationId}` } }
      : {}),
  });
  const templateRef = getGroupTemplateRef(group);
  if (templateRef?.reference) setRequestedGroupTemplateRef(draft, templateRef);
  return medplum.createResource(draft);
}

async function advanceGroupTherapyRequest(
  medplum: MedplumClient,
  serviceRequest: ServiceRequest,
  stage: TrackerStageCode,
): Promise<ServiceRequest> {
  if (readTrackerStage(serviceRequest.meta) === stage) return serviceRequest;
  return medplum.updateResource({
    ...serviceRequest,
    status: applyStageToStatus(stage).serviceRequestStatus,
    meta: upsertTrackerStageTag(serviceRequest.meta, stage),
  });
}
